'use client'

import { useEffect, useId } from 'react'

import confetti from 'canvas-confetti'
import { motion } from 'framer-motion'

import { rewardDetailLabel } from '../lib/pass-ui'
import type { PassRewardTierItem } from '../types/pass'

import { cn } from '@/shared/lib/utils'
import { Button } from '@/shared/ui/Button'
import { Dialog, DialogContent, DialogTitle } from '@/shared/ui/dialog'

import { RewardVisual } from './RewardVisual'

// canvas-confetti pinta num <canvas>, então não enxerga classe do Tailwind nem
// custom property — as cores precisam ser lidas dos tokens em runtime.
const PASS_CONFETTI_TOKENS = [
  '--pass-gold',
  '--pass-gold-soft',
  '--pass-accent',
  '--brand-pink',
  '--brand-cyan',
  '--brand-green'
]

function tokenColors(tokens: string[], fallback: string) {
  if (typeof window === 'undefined') return [fallback]

  const styles = window.getComputedStyle(document.documentElement)
  const resolved = tokens
    .map(token => styles.getPropertyValue(token).trim())
    .filter(Boolean)

  return resolved.length > 0 ? resolved : [fallback]
}

// Mesma celebração do desbloqueio de cosmético em level-play: dois jatos
// laterais contínuos + um burst de estrelas central.
function fireRevealConfetti() {
  if (
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
    return

  const duration = 2.8 * 1000
  const animationEnd = Date.now() + duration
  const defaults = {
    startVelocity: 32,
    spread: 360,
    ticks: 55,
    zIndex: 9999,
    colors: tokenColors(PASS_CONFETTI_TOKENS, 'gold')
  }
  const randomInRange = (min: number, max: number) => Math.random() * (max - min) + min

  const interval = window.setInterval(() => {
    const timeLeft = animationEnd - Date.now()
    if (timeLeft <= 0) {
      window.clearInterval(interval)
      return
    }
    const particleCount = 42 * (timeLeft / duration)
    confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.12, 0.32), y: randomInRange(0.15, 0.45) } })
    confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.68, 0.88), y: randomInRange(0.15, 0.45) } })
  }, 220)

  const starDefaults = {
    spread: 360,
    ticks: 48,
    gravity: 0.45,
    decay: 0.94,
    startVelocity: 28,
    colors: tokenColors(['--pass-gold', '--pass-gold-soft', '--brand-yellow'], 'gold'),
    zIndex: 9999
  }
  confetti({ ...starDefaults, particleCount: 45, scalar: 1.1, shapes: ['star'], origin: { x: 0.5, y: 0.42 } })
  window.setTimeout(() => {
    confetti({ ...starDefaults, particleCount: 28, scalar: 0.85, shapes: ['circle'], origin: { x: 0.5, y: 0.38 } })
  }, 120)

  return () => window.clearInterval(interval)
}

// Cada recompensa aparece pela sua própria imagem: cosmético e item de loja
// mostram a arte cadastrada, pontos mostram a estrela e moedas a moeda. Um
// tier com pontos + cosmético revela os dois lado a lado.
function RewardFace({ item, size }: { item: PassRewardTierItem; size: 'lg' | 'sm' }) {
  const box = size === 'lg' ? 'size-40 sm:size-44' : 'size-24 sm:size-28'

  return (
    <div className="flex flex-col items-center gap-2">
      <div
        className={cn(
          'flex items-center justify-center overflow-hidden rounded-2xl',
          'bg-pass-surface-inset p-2 ring-2 ring-pass-gold/40',
          'shadow-[0_0_32px_-8px_var(--pass-gold)]',
          box
        )}
      >
        <RewardVisual item={item} />
      </div>

      <span
        className={cn(
          'max-w-[11rem] text-center font-black tracking-[-0.02em] text-white',
          size === 'lg' ? 'text-[1.05rem] sm:text-[1.25rem]' : 'text-[0.9rem]'
        )}
      >
        {rewardDetailLabel(item)}
      </span>
    </div>
  )
}

interface PassRewardRevealModalProps {
  /** Tudo que o aluno recebeu neste resgate — um tier pode entregar vários. */
  items: PassRewardTierItem[]
  description: string
  onClose: () => void
}

export function PassRewardRevealModal({
  items,
  description,
  onClose
}: PassRewardRevealModalProps) {
  const titleId = useId()
  const descId = useId()
  const single = items.length === 1

  useEffect(() => fireRevealConfetti(), [])

  return (
    <Dialog open onOpenChange={open => !open && onClose()}>
      <DialogContent
        hideCloseButton
        className={cn(
          'p-0 overflow-hidden border-2 border-pass-gold/90',
          'bg-[linear-gradient(180deg,var(--pass-surface)_0%,var(--pass-surface-deep)_55%,var(--pass-surface-inset)_100%)]',
          'shadow-[0_12px_40px_rgba(0,0,0,0.55)]',
          'sm:max-w-md'
        )}
        aria-labelledby={titleId}
        aria-describedby={descId}
      >
        <div className="relative flex flex-col items-center gap-4 px-5 pb-5 pt-6">
          <span className="inline-block rounded-full border border-pass-gold/50 bg-pass-gold/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.2em] text-pass-gold-soft">
            Recompensa resgatada
          </span>

          <DialogTitle
            id={titleId}
            className="text-center text-xl font-extrabold uppercase tracking-wide text-pass-gold-soft drop-shadow-[0_1px_0_rgba(0,0,0,0.5)]"
          >
            Você recebeu!
          </DialogTitle>

          <motion.div
            className={cn(
              'flex w-full flex-wrap items-start justify-center',
              single ? 'gap-0' : 'gap-4'
            )}
            initial={{ scale: 0.7, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 260, damping: 18 }}
          >
            {items.map(item => (
              <RewardFace key={item.id} item={item} size={single ? 'lg' : 'sm'} />
            ))}
          </motion.div>

          <p id={descId} className="text-center text-sm font-medium text-white/70">
            {description}
          </p>

          <Button type="button" variant="reward" className="mt-1 w-full" onClick={onClose}>
            Continuar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
